import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { toast } from "react-toastify";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import {
  FiPaperclip,
  FiSend,
  FiArrowRight,
  FiCheckCircle,
  FiX,
  FiFileText,
} from "react-icons/fi";
import "./index.css";
import ApiServices from "../../services/apiServices";
import { useLoad } from "../../context/Load";
import Mask from "../../services/mask";
import moment from "moment";

const STATUS_FINALIZADO = 3;
const TAMANHO_MAXIMO_ANEXO = 10 * 1024 * 1024;
const MSG_ERRO = "Não foi possível concluir a ação. Tente novamente.";

function fnEhImagem(url) {
  return /\.(png|jpe?g|gif|webp|bmp)(\?|$)/i.test(url || "");
}

export default function ChamadoNovoEditar() {
  const [chamado, setChamado] = useState(null);
  const [mensagem, setMensagem] = useState("");
  const [arquivo, setArquivo] = useState(null);
  const [encaminharPara, setEncaminharPara] = useState("");
  const [confirmarFinalizar, setConfirmarFinalizar] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const historicoRef = useRef(null);
  const { setLoad } = useLoad();
  const navigate = useNavigate();
  const params = useParams();

  async function fnChamadoBuscar() {
    try {
      setLoad(true);
      let response = await ApiServices.apiGet(`faleconosco/` + params.id);
      if (response.statusCode === 200 && response.data) {
        setChamado(response.data);
      } else {
        toast.error(response.message || "Chamado não encontrado.");
        navigate("/fale-conosco/chamado");
      }
    } catch (err) {
      console.error("err", err);
      toast.error(MSG_ERRO);
      navigate("/fale-conosco/chamado");
    } finally {
      setLoad(false);
    }
  }

  // 400 = regra de negócio (ex.: chamado encerrado por outro atendente): recarrega para refletir o status atual
  async function fnTratarErroResposta(response) {
    toast.error(response.message || MSG_ERRO);
    if (response.statusCode === 400) {
      await fnChamadoBuscar();
    }
  }

  function fnSelecionarArquivo(e) {
    const file = e.target.files[0];
    e.target.value = "";
    e.target.blur();
    if (!file) return;
    if (!file.type.startsWith("image/") && file.type !== "application/pdf") {
      toast.error("Formato não permitido. Envie uma imagem ou um PDF.");
      return;
    }
    if (file.size > TAMANHO_MAXIMO_ANEXO) {
      toast.error("O arquivo deve ter no máximo 10 MB.");
      return;
    }
    setArquivo(file);
  }

  async function fnEnviar() {
    try {
      setEnviando(true);
      setLoad(true);
      let anexo = null;
      if (arquivo) {
        const formData = new FormData();
        formData.append("file", arquivo);
        const upload = await ApiServices.apiUpload("upload/faleconosco", formData);
        anexo = upload.data;
      }
      let response = await ApiServices.apiPost(
        `faleconosco/interacao/` + params.id,
        { mensagem: mensagem.trim() || null, anexo }
      );
      if (response.statusCode === 200) {
        toast.success("Mensagem enviada");
        setMensagem("");
        setArquivo(null);
        await fnChamadoBuscar();
      } else {
        await fnTratarErroResposta(response);
      }
    } catch (err) {
      console.error("err", err);
      toast.error(err.data?.message || MSG_ERRO);
    } finally {
      setEnviando(false);
      setLoad(false);
    }
  }

  async function fnEncaminhar() {
    try {
      setEnviando(true);
      setLoad(true);
      let response = await ApiServices.apiPatch(
        `faleconosco/${params.id}/status`,
        { faleConoscoStatusId: Number(encaminharPara) }
      );
      if (response.statusCode === 200) {
        toast.success("Chamado encaminhado");
        setEncaminharPara("");
        await fnChamadoBuscar();
      } else {
        await fnTratarErroResposta(response);
      }
    } catch (err) {
      console.error("err", err);
      toast.error(MSG_ERRO);
    } finally {
      setEnviando(false);
      setLoad(false);
    }
  }

  async function fnFinalizar() {
    try {
      setEnviando(true);
      setLoad(true);
      let response = await ApiServices.apiPost(
        `faleconosco/finalizar/` + params.id
      );
      if (response.statusCode === 200) {
        toast.success("Chamado finalizado");
        navigate("/fale-conosco/chamado");
      } else {
        await fnTratarErroResposta(response);
      }
    } catch (err) {
      console.error("err", err);
      toast.error(MSG_ERRO);
    } finally {
      setConfirmarFinalizar(false);
      setEnviando(false);
      setLoad(false);
    }
  }

  useEffect(() => {
    if (params.id) {
      fnChamadoBuscar();
    }
  }, []); //eslint-disable-line react-hooks/exhaustive-deps

  // rola o histórico até a última mensagem ao carregar, após cada envio e quando uma imagem de anexo termina de carregar
  function fnRolarHistorico() {
    if (historicoRef.current) {
      historicoRef.current.scrollTop = historicoRef.current.scrollHeight;
    }
  }

  useEffect(fnRolarHistorico, [chamado]);

  if (!chamado) {
    return <div className="tituloPagina">FALE CONOSCO</div>;
  }

  const respostas = chamado.respostas || [];
  const donoId = respostas[0]?.participanteId;
  const finalizado = chamado.faleConoscoStatusId === STATUS_FINALIZADO;

  return (
    <div className="chamadoDetalhe">
      <div className="tituloPagina">FALE CONOSCO</div>

      <div className="chamadoCabecalhoDetalhe">
        <FiFileText />
        <span className="chamadoProtocolo">Protocolo: {chamado.protocolo}</span>
        <span className={`chamadoBadge status${chamado.faleConoscoStatusId}`}>
          {chamado.status}
        </span>
      </div>

      <div className="row g-3">
        <div className="col-lg-4">
          <div className="chamadoCard">
            <div className="chamadoCardCabecalho">Dados do cliente</div>
            <label>Nome</label>
            <p>{chamado.nome || "-"}</p>
            <label>CPF (Username)</label>
            <p>{Mask.cpf(chamado.userName) || "-"}</p>
            <label>Concessionária</label>
            <p>{chamado.concessionaria || "Sem Concessionária Cadastrada"}</p>
            <label>Cargo</label>
            <p>{chamado.cargo || "-"}</p>
          </div>
          <div className="chamadoCard mt-3">
            <div className="chamadoCardCabecalho">Detalhe do chamado</div>
            <label>Motivo</label>
            <p>{chamado.assunto || "-"}</p>
            <label>Início</label>
            <p>{moment(chamado.dataHora).format("DD/MM/YYYY HH:mm")}</p>
          </div>
        </div>

        <div className="col-lg-8">
          <div className="chamadoCard">
            <div className="chamadoCardCabecalho">Histórico da Conversa</div>
            <div className="chamadoHistorico" ref={historicoRef}>
              {respostas.map((r) => {
                const atendente = r.participanteId !== donoId;
                return (
                  <div
                    key={r.faleConoscoInteracaoId}
                    className={`chamadoMensagem ${
                      atendente ? "atendente" : "participante"
                    }`}
                  >
                    <div className="chamadoMensagemAutor">{r.nome}</div>
                    {r.mensagem && (
                      <div className="chamadoMensagemTexto">{r.mensagem}</div>
                    )}
                    {/^https?:\/\//i.test(r.anexo || "") && (
                      <a
                        href={r.anexo}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="chamadoAnexo"
                      >
                        {fnEhImagem(r.anexo) ? (
                          <img src={r.anexo} alt="Anexo" onLoad={fnRolarHistorico} />
                        ) : (
                          <>
                            <FiPaperclip /> Anexo
                          </>
                        )}
                      </a>
                    )}
                    <div className="chamadoMensagemData">
                      {moment(r.dataCriacao).format("DD/MM/YYYY HH:mm")}
                    </div>
                  </div>
                );
              })}
            </div>

            {finalizado ? (
              <div className="chamadoSomenteConsulta">
                Chamado finalizado – somente consulta
              </div>
            ) : (
              <div className="mt-3">
                <label htmlFor="chamadoNovaMensagem" className="chamadoLabelMensagem">Nova Mensagem</label>
                <textarea
                  id="chamadoNovaMensagem"
                  className="form-control"
                  rows={4}
                  placeholder="Digite sua mensagem..."
                  value={mensagem}
                  onChange={(e) => setMensagem(e.target.value)}
                />
                <div className="chamadoAcoes d-flex align-items-center flex-wrap gap-2 mt-2">
                  <label
                    htmlFor="chamadoArquivo"
                    className="btn btn-outline-primary mb-0"
                  >
                    <FiPaperclip /> Anexar
                  </label>
                  <input
                    type="file"
                    id="chamadoArquivo"
                    accept="image/*,application/pdf"
                    style={{ display: "none" }}
                    onChange={fnSelecionarArquivo}
                  />
                  <button
                    className="btn btn-primary"
                    onClick={fnEnviar}
                    disabled={enviando || (!mensagem.trim() && !arquivo)}
                  >
                    <FiSend /> Enviar
                  </button>
                  {arquivo && (
                    <span className="chamadoArquivoNome">
                      {arquivo.name}
                      <button
                        type="button"
                        className="btn btn-link p-0 ms-2"
                        title="Remover anexo"
                        onClick={() => setArquivo(null)}
                      >
                        <FiX />
                      </button>
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="chamadoRodape d-flex align-items-center flex-wrap gap-2 mt-4 mb-5">
        <button
          className="btn btn-outline-primary"
          onClick={() => {
            navigate("/fale-conosco/chamado");
          }}
        >
          VOLTAR
        </button>
        {!finalizado && (
          <>
            <label htmlFor="chamadoEncaminhar" className="ms-md-4">
              Encaminhar para:
            </label>
            <select
              id="chamadoEncaminhar"
              className="form-select"
              value={encaminharPara}
              onChange={(e) => setEncaminharPara(e.target.value)}
            >
              <option value="">Selecione</option>
              <option value="4">Em andamento</option>
              <option value="2">Aguardando participante</option>
            </select>
            <button
              className="btn btn-primary"
              onClick={fnEncaminhar}
              disabled={enviando || !encaminharPara}
            >
              <FiArrowRight /> Encaminhar
            </button>
            <button
              className="btn btn-success ms-auto"
              onClick={() => setConfirmarFinalizar(true)}
            >
              <FiCheckCircle /> Finalizar
            </button>
          </>
        )}
      </div>

      <Dialog
        open={confirmarFinalizar}
        onClose={() => setConfirmarFinalizar(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Finalizar chamado</DialogTitle>
        <DialogContent dividers>
          Deseja finalizar o chamado? O participante será avisado por e-mail e
          o chamado ficará disponível somente para consulta.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmarFinalizar(false)}>Cancelar</Button>
          <Button
            color="success"
            variant="contained"
            onClick={fnFinalizar}
            disabled={enviando}
          >
            Finalizar
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
